import React, { Fragment, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';

import Icon from '../../base/components/Icon';
import PropControl from '../../base/components/PropControl';
import { DownloadCSVLink } from '../../base/components/SimpleTable';
import GreenTag, { KGreenTagType, KMacroType } from '../../base/data/GreenTag';
import DataStore, { getListPath } from '../../base/plumbing/DataStore';
import { assert } from '../../base/utils/assert';
import { copyTextToClipboard, doShare, isURL, stopEvent } from '../../base/utils/miscutils';
import C from '../../C';
import ActionMan from '../../plumbing/ActionMan';
import KStatus from '../../base/data/KStatus';
import List from '../../base/data/List';
import Misc from '../MiscOverrides';
import { greenTagCsvColumns } from './greenTagPageUtils';


const labelTagTypes = { PIXEL: 'Pixel', JAVASCRIPT: 'Javascript', REDIRECT: 'Redirect (to wrap another tag URL)', WRAPPER: 'VAST/VPAID wrapper' };
const labelMacroTypes = { NONE: 'No macros', DV360: 'Google DV360', GOOGLE: 'Google Ad Manager', TTD: 'TheTradeDesk', XANDR: 'Xandr / AppNexus', YAHOO: 'Yahoo', AMAZON: 'Amazon' };

const paramsPath = ['widget', 'GreenTagEditor', 'export', 'params'];


/**
 * Really want to just get the tags list from the accompanying ListLoad - but that needs a big refactor first.
 * This replicates its fetch behaviour closely enough for now.
 * @param q The search query
 * @param filter The client-side post-fetch filter
 * @returns {PromiseValue<List>} A list of Green Ad Tags
 */
const listTags = (q, filter) => {
	const listSpec = {type: C.TYPES.GreenTag, status: KStatus.PUBLISHED, q};
	return DataStore.fetch(getListPath({ ...listSpec, postFilter: filter }), () => {
		return ActionMan.list({type: C.TYPES.GreenTag, status: KStatus.PUBLISHED, q}).promise.then(res => {
			if (!filter) return res;
			// HACK Replicate the post-filtering done by ListLoad
			const filteredHits = List.hits(res).filter(item => JSON.stringify(item).toLowerCase().indexOf(filter) >= 0);
			return new List(filteredHits);
		});
	});
};


/**
 * Fetch all the "supporting items" (agency, advertiser, campaign) for a set of Green Ad Tags.
 * Used to pre-load items before allowing export-to-CSV so the items can resolve to their names, rather than IDs.
 * @param {GreenAdTag[]} tags
 * @returns {Promise} Resolves when all fetches are complete and items are in DataStore.
 */
const fetchItemsForTags = (tags) => {
	// We'll want to fetch their agencies, brands and campaigns to fill out names in the CSV.
	const toFetch = { [C.TYPES.Agency]: {}, [C.TYPES.Advertiser]: {}, [C.TYPES.Campaign]: {} };
	// DataStore.getData resolves PUB_OR_DRAFT to DRAFT (probably a bug) but ActionMan.list will fetch PUBLISHED for preference
	// So for now - always fetch DRAFT to avoid "we have published but looked for draft" errors
	const status = KStatus.DRAFT;
	// Compile IDs of items to fetch from all tags - but skip items that are already in the store
	tags.forEach(({agencyId, vertiser, campaign}) => {
		const itemsForTag = [
			{type: C.TYPES.Agency, id: agencyId},
			{type: C.TYPES.Advertiser, id: vertiser},
			{type: C.TYPES.Campaign, id: campaign}
		];
		itemsForTag.forEach(({type, id}) => {
			if (!id) return; // No item = no fetch
			if (DataStore.getData({status, type, id})) return; // No need to fetch, already have it
			toFetch[type][id] = true;
		});
	});
	// Do the fetch! One for each type.
	const allFetchPromises = [C.TYPES.Agency, C.TYPES.Advertiser, C.TYPES.Campaign].map(type => {
		const ids = Object.keys(toFetch[type]).sort(); // Sort IDs to ensure the DataStore path is stable for best caching

		return ActionMan.list({type, status, ids}).promise.then(res => {
			List.hits(res).forEach(item => DataStore.setData({status, item, update: false}));
		});
	});
	// Combine the per-type fetches into one promise.
	return Promise.all(allFetchPromises);
};


/** Generate pixel etc for one tag at a time */
function TagExportSingle({tag}) {
	if (!tag) return null;

	const exportParams = DataStore.getValue(paramsPath) || {};
	const tagExport = GreenTag.generate({...tag, ...exportParams});

	const copyTag = (e) => {
		stopEvent(e);
		copyTextToClipboard(tagExport);
		return false;
	};

	// Share button won't be shown if the browser doesn't support it as an action
	const shareTag = (e) => {
		stopEvent(e);
		doShare(tagExport);
		return false;
	};

	return <>
		<p className="mb-0">Tag export:</p>
		<div className="export-box">
			<div className="tag-export p-1 mr-2">{tagExport}</div>
			<div className="export-buttons">
				<Button size="sm" color="default" onClick={copyTag} className="mb-1">
					<Icon name="clipboard" /> Copy
				</Button>
				{navigator.share ? (
					<Button size="sm" color="default" onClick={shareTag}>
						<Icon name="share" /> Share
					</Button>
				) : null}
			</div>
		</div>
	</>;
}


/** Generate pixels etc for an array of tags and put name, creative-URL, and export-tag in a downloadable CSV */
function TagExportMultiple({tags}) {
	const [tableData, setTableData] = useState();
	const exportParams = DataStore.getValue(paramsPath) || {};

	useEffect(() => {
		setTableData(tags.map(tag => ({
			...tag, Export: GreenTag.generate({...tag, ...exportParams})
		})));
	}, [tags, exportParams]);

	if (!tableData) return null;

	return <DownloadCSVLink columns={[...greenTagCsvColumns, 'Export']} data={tableData} />;
}


/** PropControls for setting the export parameters */
function TagExportControls() {
	const { tagType } = DataStore.getValue(paramsPath) || {};

	return <>
		<Row>
			<Col sm="6">
				<PropControl type="select" path={paramsPath} prop="tagType" label="Export Tag Type"
					options={[KGreenTagType.PIXEL, KGreenTagType.JAVASCRIPT, KGreenTagType.REDIRECT]}
					labels={labelTagTypes} dflt={KGreenTagType.PIXEL}
					help="What kind of tag would you like to export?"
				/>
			</Col>
			<Col sm="6">
				<PropControl type="select" path={paramsPath} prop="macroType" label="Macro Format"
					options={KMacroType.values} labels={labelMacroTypes} dflt={null}
					help="Add DSP macros?"
				/>
			</Col>
		</Row>
		{tagType === KGreenTagType.REDIRECT && <Row>
			<Col xs="12">
				<p>The redirect tag logs an impression - then redirects to another URL. You can use it to piggyback Green Ad Tag logging on another pixel, or to log clickthroughs instead of impressions.</p>
				<PropControl type="url" path={paramsPath} prop="wrapped" label="Target URL for redirect"
					help=""
				/>
			</Col>
		</Row>}
	</>;
}


/** @returns {String[]} Descriptions of problems found with the selected export parameters */
const checkExportParams = () => {
	const errors = [];

	const {tagType, wrapped, macroType} = DataStore.getValue(paramsPath) || {};

	if (tagType === KGreenTagType.REDIRECT) {
		if (!wrapped) {
			errors.push('Set your target URL to generate a redirect tag.');
		} else if (!isURL(wrapped)) {
			errors.push('Redirect target URL is not valid.');
		}
	}
	if (!macroType) {
		errors.push('Select a macro format (or "No macros") before exporting.');
	}

	return errors;
};


/**
 * Check export params are OK and display the appropriate single or multiple exporter.
*/
function TagExport({tag, tags}) {
	// Any reason we can't export the tag(s)? Check the parameters.
	const errors = checkExportParams();
	if (errors?.length) {
		return errors.map(e => <Alert key={e} color="warning" className="p-2 mb-2">{e}</Alert>);
	}

	return (tags?.length) ? (
		<TagExportMultiple tags={tags} />
	) : (
		<TagExportSingle tag={tag} />
	);
}


/** Take one or several Green Ad Tags, ask the user what export format they want, and generate it. */
function GreenTagExporter({tag, tags, ...props}) {
	assert(tag || tags);

	const [open, setOpen] = useState();
	const toggle = () => setOpen(!open);

	// Are we generating a downloadable CSV export for a batch, or just one tag?
	const isMultiple = tags?.length;

	return <>
		<Button color="default" {...props} onClick={toggle}>{isMultiple ? 'Export .CSV' : 'Export'}</Button>
		<Modal id="green-tag-exporter" size="lg" isOpen={open} toggle={toggle}>
			<ModalHeader>
				{isMultiple ? <>Exporting {tags.length} tags to .CSV </> : <>Export tags for <strong>{tag.name}</strong></>}
			</ModalHeader>
			<ModalBody>
				<p>Choose your tag type and macro format to export.</p>
				<Container>
					<TagExportControls />
					<Row>
						<Col xs="12">
							<TagExport tag={tag} tags={tags} />
						</Col>
					</Row>
				</Container>
			</ModalBody>
		</Modal>
	</>;
}


/** Loads a list of tags using similar params to ListLoad and provides a CSV download with pixel/script/etc exports */
export function BulkTagExport({q, filter}) {
	const [open, setOpen] = useState(false);
	const toggle = () => setOpen(!open);

	const pvTags = listTags(q, filter);
	const tags = List.hits(pvTags?.value);

	// Do we have all the supporting items needed to generate a CSV export?
	const [csvReady, setCsvReady] = useState(false);
	useEffect(() => {
		if (!open) return; // Don't kick off the fetch until the export modal is opened.
		setCsvReady(false); // The list has changed, so invalidate previous successful fetches
		if (!tags) return;
		fetchItemsForTags(tags).then(() => {
			setCsvReady(true); // Fetch complete - ready to generate CSV.
		});
	}, [open, q, filter]);

	// Don't show the export controls until the supporting items are downloaded.
	let headerContent, bodyContent;
	if (tags?.length === 0) {
		headerContent = 'No tags to export';
		bodyContent = <>Adjust your filter settings to include some tags before exporting.</>;
	} else {
		if (!pvTags.resolved) {
			headerContent = <>Exporting current tag list</>;
			bodyContent = <Misc.Loading text="Fetching Green Ad Tags for export..." />;
		} else if (!csvReady) {
			headerContent = <>Exporting current tag list</>;
			bodyContent = <Misc.Loading text="Preparing CSV download..." />;
		} else {
			headerContent = <>Exporting {tags.length} tags</>;
			bodyContent = <Container>
				<TagExportControls />
				<TagExport tags={tags} />
			</Container>;
		}
	}

	return <>
		<Button color="default" onClick={toggle}>Export</Button>
		<Modal isOpen={open} toggle={toggle}>
			<ModalHeader>{headerContent}</ModalHeader>
			<ModalBody>
				{bodyContent}
			</ModalBody>
		</Modal>
	</>;
}


export default GreenTagExporter;
